import type {
  AgenticLogicRuleDto,
  AgenticLogicRulePublishJobDto,
  AgenticLogicTemplateSummaryDto,
} from "@intelligems/ig-types";
import { stripMigrationProvenanceMarker } from "@intelligems/ig-types";
import { IgAlert, IgLoading, IgTypography } from "@intelligems/ig-theme";
import { Box, Stack } from "@mui/material";
import { useImmer } from "use-immer";

import { useAgenticLogicRule } from "@src/api/agentic-logic/get-agentic-logic-rule";
import { useAgenticLogicRuleVersions } from "@src/api/agentic-logic/get-agentic-logic-rule-versions";
import { useAgenticLogicTemplates } from "@src/api/agentic-logic/list-agentic-logic-templates";
import { useUpdateAgenticLogicRule } from "@src/api/agentic-logic/update-agentic-logic-rule";

import { RuleSettingsFields } from "./RuleSettingsFields";
import { RuleVersionsTab } from "./RuleVersionsTab";
import { SavedRuleDraft } from "./SavedRuleDraft";
import { UnpublishedDraftCallout } from "./UnpublishedDraftCallout";
import { useRuleLiveness } from "./useRuleLiveness";
import { useUnpublishedRuleChanges } from "./useUnpublishedRuleChanges";
import { isWorkspaceSaveDisabled } from "@src/features/agenticCodegen/workspace/saveAvailability";
import { DiscardChangesDialog } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/DiscardChangesDialog";
import { useUnsavedWorkspaceGuard } from "@src/features/agenticCodegen/workspace/components/00-PageFrame/useUnsavedWorkspaceGuard";

import { TemplateSelectionLogicTab } from "../graph/TemplateSelectionLogicTab";
import { ruleWorkspaceKind } from "../surface/surface";
import { TemplateConfigConflictBanner } from "../template-config/TemplateConfigConflictBanner";
import { TemplateRuleActions } from "../template-config/TemplateRuleActions";
import { TemplateRuleAdvancedTab } from "../template-config/TemplateRuleAdvancedTab";
import { TemplateVersionConfig } from "../template-config/TemplateVersionConfig";
import { useTemplateRuleWorkspace } from "../template-config/useTemplateRuleWorkspace";
import { RuleDescriptionEditor } from "../rules/RuleDescriptionEditor";
import { AgenticLogicRuleWorkspacePage } from "./AgenticLogicRuleWorkspacePage";
import { RuleDetailPageView } from "./RuleDetailPageView";
import { resolveRuleExitMode } from "./ruleExitMode";
import { DEFAULT_TEMPLATE_RULE_DETAIL_TAB } from "./ruleDetailTabs";
import { useRuleDetailTab } from "./useRuleDetailTab";
import { useRuleDetailByline } from "./useRuleDetailByline";
import { useAgenticLogicSurface } from "@src/features/ai/pages/agentic-logic/surface/useAgenticLogicSurface";
import { useSurfaceNavigate } from "@src/features/ai/pages/agentic-logic/surface/useSurfaceNavigate";
import { useIsAdvancedAgenticLogicEnabled } from "@src/hooks/feature-flags/useIsAdvancedAgenticLogicEnabled";
import { useRootPageCurrentCrumb } from "@src/utils/useRootPageCurrentCrumb";

const CenteredLoading = () => (
  <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
    <IgLoading />
  </Stack>
);

export const CenteredErrorState = ({
  title,
  message,
}: {
  title: string;
  message: string;
}) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    spacing={1}
    sx={{ height: "100%", px: 4, textAlign: "center" }}
  >
    <IgTypography variant="body1" fontWeight={600}>
      {title}
    </IgTypography>
    <IgTypography color="text.secondary" sx={{ maxWidth: 480 }}>
      {message}
    </IgTypography>
  </Stack>
);

/**
 * A template-managed rule in the SAME page frame as a code rule: one Selection
 * logic canvas, one Advanced tab. A merchant cannot tell from the URL which
 * flavour a rule is and must not meet two different pages at one route — only
 * the Advanced body differs, config JSON here and the code workspace there.
 */
const TemplateRulePage = ({
  rule,
  template,
  latestPublishJob,
}: {
  rule: AgenticLogicRuleDto;
  template: AgenticLogicTemplateSummaryDto;
  latestPublishJob: AgenticLogicRulePublishJobDto | null;
}) => {
  const navigate = useSurfaceNavigate();
  const surface = useAgenticLogicSurface();
  const updateRule = useUpdateAgenticLogicRule();
  const advancedEnabled = useIsAdvancedAgenticLogicEnabled();
  const [selectedTab, setSelectedTab] = useRuleDetailTab(
    DEFAULT_TEMPLATE_RULE_DETAIL_TAB,
    advancedEnabled,
    surface.tabParam
  );
  const byline = useRuleDetailByline(rule);
  // Atlas writes the rule row without deploying it, and — since Save stopped
  // deploying a rule that is serving — so does the merchant's own Save. Either
  // way the saved configuration can be sitting here unseen by shoppers, and
  // the callout's Publish is what closes the gap.
  const { hasUnpublishedChanges, comparisonFailed } =
    useUnpublishedRuleChanges(rule);
  // Whether Save may deploy. One helper for both rule flavours, so a template
  // rule and a code rule cannot disagree about what "live" means.
  const { liveness, saveAction } = useRuleLiveness(rule.id);
  // The expanded version cards' source view. Params ride the version LIST DTO
  // the history already holds, so reading them here costs no extra request.
  const versionsQuery = useAgenticLogicRuleVersions(rule.id);

  const editor = useTemplateRuleWorkspace({
    mode: { kind: "edit", rule },
    template,
    latestPublishJob,
    onCreated: () => undefined,
  });
  const { workspace } = editor;
  const [confirmingExit, setConfirmingExit] = useImmer(false);
  const exitMode = resolveRuleExitMode({ isDirty: workspace.isDirty });
  const leaveGuard = useUnsavedWorkspaceGuard(exitMode !== null);
  // Mirrors the code workspace's hint: saving is what deploys, so a publish
  // already in flight is the one blocker worth naming. Every OTHER term that
  // disables Save has to be named here too — a Save that is disabled for an
  // unnamed reason is the defect this hint exists for.
  const resolveSaveHint = (): string | undefined => {
    if (editor.isPublishing) {
      return "Wait for the current publish to finish.";
    }
    if (workspace.hasParamsConflict) {
      return "Resolve the configuration conflict before saving.";
    }
    if (!workspace.isSourceValid) {
      return "Fix the configuration before saving.";
    }
    return undefined;
  };
  const saveHint = resolveSaveHint();
  const goBack = () => {
    if (exitMode) {
      setConfirmingExit(true);
    } else {
      navigate(surface.exitPath);
    }
  };

  return (
    <>
      <DiscardChangesDialog
        open={confirmingExit || leaveGuard.isBlocked}
        entityLabel="rule"
        entityName={workspace.name}
        onKeepEditing={() => {
          setConfirmingExit(false);
          leaveGuard.reset();
        }}
        onDiscard={() => {
          setConfirmingExit(false);
          if (leaveGuard.isBlocked) {
            leaveGuard.proceed();
          } else {
            navigate(surface.exitPath);
          }
        }}
      />
      <RuleDetailPageView
        backLabel={surface.backLabel}
        name={workspace.name}
        description={stripMigrationProvenanceMarker(rule.description)}
        eligibleSurfaces={byline.eligibleSurfaces}
        usedIn={byline.usedIn}
        isRenaming={updateRule.isPending}
        isSavingDescription={updateRule.isPending}
        selectedTab={selectedTab}
        onSelectedTabChange={setSelectedTab}
        onRename={async (name) => {
          await updateRule.mutateAsync({
            body: { name },
            pathParams: { ruleId: rule.id },
            queryParams: undefined,
          });
          // The config workspace persists the name alongside the params, so it
          // has to adopt the one the frame just wrote.
          workspace.syncPersistedName(name);
        }}
        onSaveDescription={async (description) => {
          await updateRule.mutateAsync({
            body: { description },
            pathParams: { ruleId: rule.id },
            queryParams: undefined,
          });
        }}
        onBack={goBack}
        advancedEnabled={advancedEnabled}
        banners={
          <>
            {comparisonFailed ? (
              <Box sx={{ px: 2, pt: 2 }}>
                <IgAlert
                  alertKey="template-publication-comparison-failed"
                  severity="warning"
                  title="Published version could not be checked"
                  message="Your draft is saved. Reload to check its publication status."
                />
              </Box>
            ) : null}
            {hasUnpublishedChanges ? (
              <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <UnpublishedDraftCallout
                  isLive={liveness === "live"}
                  isPublishing={editor.isPublishing}
                  onPublish={() => void editor.publish()}
                  // Publishing deploys what is PERSISTED, so an edited buffer
                  // would ship the previous configuration.
                  disabledHint={
                    workspace.isDirty
                      ? "Save your changes before publishing."
                      : undefined
                  }
                />
              </Box>
            ) : null}
            {workspace.conflictParams !== null ? (
              <TemplateConfigConflictBanner
                conflictParams={workspace.conflictParams}
                onTakeTheirs={() =>
                  workspace.resolveParamsConflict("take_theirs")
                }
                onKeepMine={() => workspace.resolveParamsConflict("keep_mine")}
              />
            ) : null}
          </>
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            {workspace.isDirty ? (
              <IgTypography variant="body2">Unsaved changes</IgTypography>
            ) : null}
            <TemplateRuleActions
              saveLabel={
                saveAction === "save_and_publish"
                  ? "Save and publish"
                  : workspace.isDirty
                    ? "Save draft"
                    : "Draft saved"
              }
              isSettled={!workspace.isDirty}
              // The shared definition owns every term it knows about; only the
              // template-only buffer completeness (a name) stays local. Notably
              // A clean draft needs Publish, not another Save.
              isSaveDisabled={
                isWorkspaceSaveDisabled({
                  isReadOnly: false,
                  isSavingDraft: editor.isSaving,
                  isPublishing: editor.isPublishing,
                  isSyntaxValid: workspace.isSourceValid,
                  // JSON parses synchronously: there is no verdict to wait for.
                  isSyntaxPending: false,
                }) ||
                !workspace.canPersist ||
                (!workspace.isDirty && saveAction === "save_draft_only")
              }
              disabledActionHint={saveHint}
              onSave={() => void editor.save(saveAction)}
            />
          </Stack>
        }
        selectionLogic={<TemplateSelectionLogicTab rule={rule} />}
        // Edits live in the workspace and land with Save, alongside the params
        // — one dirty state, one publish.
        displaySettings={
          <RuleSettingsFields
            settings={workspace.settings}
            saving={editor.isSaving}
            onChange={workspace.setSettings}
          />
        }
        versions={
          <RuleVersionsTab
            ruleId={rule.id}
            // Template drafts are the saved parameters and settings on the rule row.
            draft={{
              lastUpdateTs: rule.lastUpdateTs,
              updatedByUserId: null,
              updatedByUser: null,
              origin: null,
            }}
            draftContent={
              <SavedRuleDraft rule={rule} advancedEnabled={advancedEnabled} />
            }
            source={
              advancedEnabled
                ? {
                    label: "Configuration",
                    render: (version) => (
                      <TemplateVersionConfig
                        version={
                          versionsQuery.data?.versions.find(
                            (candidate) => candidate.version === version
                          ) ?? null
                        }
                      />
                    ),
                  }
                : undefined
            }
          />
        }
        advanced={
          <TemplateRuleAdvancedTab
            ruleId={rule.id}
            editor={editor}
            descriptionEditor={
              <RuleDescriptionEditor
                description={rule.description}
                saving={updateRule.isPending}
                onSave={async (description) => {
                  await updateRule.mutateAsync({
                    body: { description },
                    pathParams: { ruleId: rule.id },
                    queryParams: undefined,
                  });
                }}
              />
            }
          />
        }
      />
    </>
  );
};

/** Resolves the rule's template before the page can render it. */
const TemplateRulePageLoader = ({
  rule,
  latestPublishJob,
}: {
  rule: AgenticLogicRuleDto;
  latestPublishJob: AgenticLogicRulePublishJobDto | null;
}) => {
  const surface = useAgenticLogicSurface();
  const templatesQuery = useAgenticLogicTemplates();

  if (templatesQuery.isLoading) {
    return <CenteredLoading />;
  }
  if (templatesQuery.isError) {
    return (
      <CenteredErrorState
        title="Templates could not be loaded"
        message={`We could not load the templates for this rule. Return to ${surface.title} and try again.`}
      />
    );
  }

  const template = templatesQuery.data?.templates.find(
    (candidate) => candidate.id === rule.templateId
  );
  if (!template) {
    return (
      <CenteredErrorState
        title="Template is not available"
        message={`This rule uses a template that is no longer available. Return to ${surface.title} to select another rule.`}
      />
    );
  }

  return (
    <TemplateRulePage
      key={rule.id}
      rule={rule}
      template={template}
      latestPublishJob={latestPublishJob}
    />
  );
};

export const AgenticLogicRulePage = ({ ruleId }: { ruleId: string }) => {
  const surface = useAgenticLogicSurface();
  const ruleQuery = useAgenticLogicRule(ruleId);
  useRootPageCurrentCrumb(ruleQuery.data?.rule?.name);

  if (ruleQuery.isLoading) {
    return <CenteredLoading />;
  }
  if (ruleQuery.isError || !ruleQuery.data?.rule) {
    return (
      <CenteredErrorState
        title="Rule could not be loaded"
        message={`We could not load this rule. Return to ${surface.title} and try again.`}
      />
    );
  }

  const { rule } = ruleQuery.data;

  switch (ruleWorkspaceKind(rule.managedBy)) {
    // Every rule state: the last gate run is the durable record of what
    // shipped, so it survives the rule leaving `draft`.
    case "template":
      return (
        <TemplateRulePageLoader
          rule={rule}
          latestPublishJob={ruleQuery.data.latestPublishJob}
        />
      );
    case "code":
      return <AgenticLogicRuleWorkspacePage rule={rule} />;
  }
};
